import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import React, {useCallback, useMemo, useState} from 'react';
import {twMerge} from 'tailwind-merge';

import {useDispatch} from '../../../../state/hooks';
import {usePanelSearchDebouncedContext} from '../../../../state/panelbank/PanelSearchContextProvider';
import * as PanelBankSectionConfigActions from '../../../../state/views/panelBankSectionConfig/actions';
import * as PanelBankSectionConfigTypes from '../../../../state/views/panelBankSectionConfig/types';
import {SectionSettings} from '../../../SettingControls/SectionSettings';
import {usePanelBankSectionIsOpen} from '../../hooks/panelBankSectionIsOpenHooks';
import {getDndSortableSectionId, getIsActiveSection} from '../utils';
import {PanelBankSectionOverflowMenu} from './PanelBankSectionOverflowMenu';
import {PanelBankSectionPagination} from './PanelBankSectionPagination';
import {PanelBankSectionTitle} from './PanelBankSectionTitle';

export const PanelBankSectionHeader = ({
  activePanelCount,
  isProjectReadOnly,
  isSectionNameSearchMatch,
  panelBankSectionConfigRef,
  isHiddenPanelsSection,
  panelSearchMatchCount = 0,
  sectionComponentRef,
  sectionName,
}: {
  activePanelCount: number;
  isProjectReadOnly?: boolean;
  isSectionNameSearchMatch?: boolean;
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  isHiddenPanelsSection: boolean;
  panelSearchMatchCount?: number;
  sectionComponentRef: React.RefObject<HTMLDivElement>;

  sectionName: string;
}) => {
  const [isEditingName, setIsEditingName] = useState(false);

  const {
    active,
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id: getDndSortableSectionId(panelBankSectionConfigRef),
    data: {
      // This helps indicate if we're dragging a section or panel
      type: 'section',
      sectionRef: panelBankSectionConfigRef,
      sectionName,
      filteredPanelsCount: panelSearchMatchCount,
    },
  });

  const {sectionIsOpen, toggleSectionIsOpen} = usePanelBankSectionIsOpen({
    panelBankSectionConfigRef,
  });
  const {isSearching} = usePanelSearchDebouncedContext();

  const dispatch = useDispatch();
  const updateName = useCallback(
    (newName: string) => {
      dispatch(
        PanelBankSectionConfigActions.updateName(
          panelBankSectionConfigRef,
          newName
        )
      );
    },
    [dispatch, panelBankSectionConfigRef]
  );

  const isActiveSection = useMemo(() => {
    return getIsActiveSection(active, panelBankSectionConfigRef);
  }, [active, panelBankSectionConfigRef]);

  // We want just the section header to be the sortable item because we "collapse" sections when
  // a section is being dragged. This allows the sortable context to know how tall the item and shift items around.
  return (
    <div
      className={twMerge(
        'flex h-[48px] items-center',
        'px-12 py-4',
        // Ensure currently dragged section has a higher z-index so it gets dragged OVER other sections.
        // We set contents of the header to be invisible when section is dragged for a better section drag experience.
        isActiveSection
          ? 'z-[1] !cursor-grabbing bg-moon-100 dark:bg-moon-100'
          : 'z-[0]'
      )}
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}>
      {!isHiddenPanelsSection && (
        <Button
          ref={setActivatorNodeRef}
          {...listeners}
          {...attributes}
          aria-label="Drag section"
          className={twMerge(
            'cursor-grab active:cursor-grabbing',
            isActiveSection && 'opacity-0'
          )}
          icon="drag-grip"
          size="small"
          variant="ghost"
        />
      )}
      <div
        className={twMerge(
          'panel-bank__section-title',
          'w-full',
          isActiveSection && 'opacity-0'
        )}>
        <span
          className="panel-bank__section-title-left"
          onClick={e => {
            // Important note: we do some css padding hacks to increase clickable area to toggle section right now.
            // user should be able to click on icon or title to toggle section
            toggleSectionIsOpen();
            e.stopPropagation();
          }}>
          <Button
            icon={sectionIsOpen ? 'chevron-down' : 'chevron-next'}
            variant="ghost"
            size="small"
            className="mr-8"
            aria-label={`${
              sectionIsOpen
                ? `Collapse ${sectionName} section`
                : `Expand ${sectionName} section`
            }`}
          />
          <PanelBankSectionTitle
            activePanelCount={activePanelCount}
            isEditingName={isEditingName}
            isSearching={isSearching}
            isSectionNameSearchMatch={isSectionNameSearchMatch ?? false}
            panelBankSectionConfigRef={panelBankSectionConfigRef}
            panelSearchMatchCount={panelSearchMatchCount}
            sectionName={sectionName}
            setIsEditingName={setIsEditingName}
            updateName={updateName}
          />
        </span>
        {!isHiddenPanelsSection && (
          <>
            <div
              className="panel-bank__section-title-right"
              style={sectionIsOpen ? {} : {visibility: 'hidden'}}>
              <PanelBankSectionPagination
                activePanelCount={
                  isSectionNameSearchMatch
                    ? activePanelCount
                    : panelSearchMatchCount
                }
              />
              <SectionSettings
                panelBankSectionConfigRef={panelBankSectionConfigRef}
                sectionComponentRef={sectionComponentRef}
              />
              <PanelBankSectionOverflowMenu
                isProjectReadOnly={isProjectReadOnly}
                panelBankSectionConfigRef={panelBankSectionConfigRef}
                sectionComponentRef={sectionComponentRef}
                setIsEditingName={setIsEditingName}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
